import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFieldArray, useFormContext, type UseFieldArrayReturn } from "react-hook-form";

import { ReactComponent as TrashIcon } from "assets/images/iconsV2/trash-01.svg";
import { useGetCountries } from "api/queries/common/useGetCountries";
import { useGetSeaports } from "api/queries/common/useGetSeaports";
import FormField from "components/molecules/FormField/FormField";
import { handleRenderAddressOption } from "helpers/schedulehelper";
import PickupDeliveryDate from "pages/shipper/RequestForm/components/Schedule/PickupDeliveryDate";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import { ISeaportOption } from "types/Common";
import { IEditScheduleForm } from "../types";

const defaultVesselInfo = {
  vessel_number: "",
  delivery_date: "",
  vessel_arrival_date: "",
  departure_city: "",
  departure_country_code: "",
  departure_country_name: "",
  arrival_port_code: "",
  arrival_port_name: "",
  arrival_city: "",
  arrival_country_code: "",
  arrival_country_name: "",
  departure_port_code: "",
  departure_port_name: "",
};

type VesselEntryProps = {
  index: number;
  countries: any[];
  remove: UseFieldArrayReturn["remove"];
};

function VesselEntry({ index, countries, remove }: VesselEntryProps) {
  const intl = useIntl();
  const [holidays, setHolidays] = useState({});
  const [departureSearchPort, setDepartureSearchPort] = useState("");
  const [arrivalSearchPort, setArrivalSearchPort] = useState("");
  const hasInitializedDepartureFromCode = useRef(false);
  const hasInitializedArrivalFromCode = useRef(false);
  const hasUserSelectedDeparturePort = useRef(false);
  const hasUserSelectedArrivalPort = useRef(false);
  const fieldPrefix = `vessels.${index}.`;
  const methods = useFormContext<IEditScheduleForm>();
  const { getValues, setValue, trigger, watch } = methods;
  const initialDeparturePortCodeRef = useRef(getValues(`${fieldPrefix}departure_port_code`) || "");
  const initialArrivalPortCodeRef = useRef(getValues(`${fieldPrefix}arrival_port_code`) || "");
  const isHydratingDepartureFromCodeRef = useRef(Boolean(initialDeparturePortCodeRef.current));
  const isHydratingArrivalFromCodeRef = useRef(Boolean(initialArrivalPortCodeRef.current));
  const departurePortCode = watch(`${fieldPrefix}departure_port_code`);
  const arrivalPortCode = watch(`${fieldPrefix}arrival_port_code`);
  const [departureCity, departureCountryName, arrivalCity, arrivalCountryName] = watch([
    `${fieldPrefix}departure_city`,
    `${fieldPrefix}departure_country_name`,
    `${fieldPrefix}arrival_city`,
    `${fieldPrefix}arrival_country_name`,
  ]);

  const { data: departureSeaports = [], isFetching: isDepartureSeaportsLoading } = useGetSeaports(departureSearchPort);
  const { data: arrivalSeaports = [], isFetching: isArrivalSeaportsLoading } = useGetSeaports(arrivalSearchPort);

  useEffect(() => {
    if (isHydratingDepartureFromCodeRef.current && initialDeparturePortCodeRef.current) {
      setDepartureSearchPort(initialDeparturePortCodeRef.current);
    }
  }, []);

  useEffect(() => {
    if (
      !isHydratingDepartureFromCodeRef.current ||
      !departurePortCode ||
      hasInitializedDepartureFromCode.current ||
      hasUserSelectedDeparturePort.current ||
      !departureSeaports.length
    )
      return;
    const initialPort = departureSeaports[0];
    setValue(`${fieldPrefix}departure_port_name`, initialPort.name, { shouldValidate: true });
    setValue(`${fieldPrefix}departure_port_code`, initialPort.un_locode, { shouldValidate: true });
    setValue(`${fieldPrefix}departure_city`, initialPort.city_name || "", { shouldValidate: true });
    setValue(`${fieldPrefix}departure_country_code`, initialPort.country_iso_code || "", { shouldValidate: true });
    setValue(`${fieldPrefix}departure_country_name`, initialPort.country_name || "", { shouldValidate: true });
    hasInitializedDepartureFromCode.current = true;
    isHydratingDepartureFromCodeRef.current = false;
  }, [departurePortCode, departureSeaports, fieldPrefix, setValue]);

  useEffect(() => {
    if (isHydratingArrivalFromCodeRef.current && initialArrivalPortCodeRef.current) {
      setArrivalSearchPort(initialArrivalPortCodeRef.current);
    }
  }, []);

  useEffect(() => {
    if (
      !isHydratingArrivalFromCodeRef.current ||
      !arrivalPortCode ||
      hasInitializedArrivalFromCode.current ||
      hasUserSelectedArrivalPort.current ||
      !arrivalSeaports.length
    )
      return;
    const initialPort = arrivalSeaports[0];
    setValue(`${fieldPrefix}arrival_port_name`, initialPort.name, { shouldValidate: true });
    setValue(`${fieldPrefix}arrival_port_code`, initialPort.un_locode, { shouldValidate: true });
    setValue(`${fieldPrefix}arrival_city`, initialPort.city_name || "", { shouldValidate: true });
    setValue(`${fieldPrefix}arrival_country_code`, initialPort.country_iso_code || "", { shouldValidate: true });
    setValue(`${fieldPrefix}arrival_country_name`, initialPort.country_name || "", { shouldValidate: true });
    hasInitializedArrivalFromCode.current = true;
    isHydratingArrivalFromCodeRef.current = false;
  }, [arrivalPortCode, arrivalSeaports, fieldPrefix, setValue]);

  const mapSeaportOptions = (ports: ISeaportOption[]) =>
    ports.map((option) => ({
      id: option.id,
      label: option.name,
      value: option.name,
      city_name: option.city_name,
      country_name: option.country_name,
      country_code: option.country_code,
      country_iso_code: option.country_iso_code,
      code: option.un_locode,
    }));

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between">
        <p className="font-b4 mb-0">
          <FormattedMessage id="quote.provider.schedule.vessel" defaultMessage="Vessel" />
          <span className="ml-1">{index + 1}</span>
        </p>
        <button
          type="button"
          className="delete-vessel-icon btn btn-link p-0"
          onClick={() => remove(index)}
          aria-label={intl.formatMessage({ id: "general.remove", defaultMessage: "Remove" })}
        >
          <TrashIcon width={20} height={20} />
        </button>
      </div>
      <div className="mb-3">
        <div className="w-192">
          <FormField
            validations={{
              shouldUnregister: true,
            }}
            id={`id_edit_schedule_vessel_number_${index}`}
            label={intl.formatMessage({
              id: "quote.provider.vesselNumber",
              defaultMessage: "Vessel number / IMO",
            })}
            type="textinput"
            field_id={`vessels.${index}.vessel_number`}
          />
        </div>
      </div>

      <div className="mb-3">
        <div className="font-b3 mb-xxs" style={{ fontWeight: 400 }}>
          Departure City
        </div>
        <div className="font-b3 mb-2" style={{ fontWeight: 700 }}>
          {[departureCity, departureCountryName].filter(Boolean).join(", ") || "-"}
        </div>
      </div>

      <div className="flex-row mb-3">
        <div className="w-192 dropdown-div">
          <SearchSelect
            shouldUnregister
            preselect={false}
            name={`${fieldPrefix}departure_port_name`}
            defaultValue={getValues(`${fieldPrefix}departure_port_name`)}
            placeholder={intl.formatMessage({
              id: "report.search",
              defaultMessage: "Search",
            })}
            onInputChange={(val: string) => {
              isHydratingDepartureFromCodeRef.current = false;
              setDepartureSearchPort(val);
            }}
            options={mapSeaportOptions(departureSeaports)}
            onSelect={(item: any[]) => {
              hasUserSelectedDeparturePort.current = true;
              isHydratingDepartureFromCodeRef.current = false;
              const selected = item?.[0];
              setValue(`${fieldPrefix}departure_port_code`, selected?.code || "", { shouldValidate: true });
              setValue(`${fieldPrefix}departure_port_name`, selected?.value || "", { shouldValidate: true });
              setValue(`${fieldPrefix}departure_city`, selected?.city_name || "", { shouldValidate: true });
              setValue(`${fieldPrefix}departure_country_code`, selected?.country_iso_code || "", { shouldValidate: true });
              setValue(`${fieldPrefix}departure_country_name`, selected?.country_name || "", { shouldValidate: true });
              trigger(`${fieldPrefix}departure_port_name`);
            }}
            filterBy={["label", "city_name", "country_name", "code"]}
            handleRenderOption={handleRenderAddressOption}
            isLoading={isDepartureSeaportsLoading}
            label={intl.formatMessage({
              id: "tracking.components.editScheduleDialog.departurePort",
              defaultMessage: "Departure port",
            })}
            required
          />
        </div>
        <div className="w-192 align-date">
          <div className="font-b3 mb-2">
            <FormattedMessage id="quote.provider.vesselDepartureDate" defaultMessage="Vessel departure date" />
            <span className="text-danger ml-1">*</span>
          </div>
          <PickupDeliveryDate
            datePickerProps={{
              name: `vessels.${index}.delivery_date`,
              required: true,
              shouldUnregister: true,
            }}
            countries={countries}
            holidays={holidays}
            setHolidays={setHolidays}
          />
        </div>
      </div>

      <div className="mb-3">
        <div className="font-b3 mb-xxs" style={{ fontWeight: 400 }}>
          Arrival City
        </div>
        <div className="font-b3 mb-2" style={{ fontWeight: 700 }}>
          {[arrivalCity, arrivalCountryName].filter(Boolean).join(", ") || "-"}
        </div>
      </div>

      <div className="flex-row mb-3">
        <div className="w-192 dropdown-div">
          <SearchSelect
            shouldUnregister
            preselect={false}
            name={`${fieldPrefix}arrival_port_name`}
            defaultValue={getValues(`${fieldPrefix}arrival_port_name`)}
            placeholder={intl.formatMessage({
              id: "report.search",
              defaultMessage: "Search",
            })}
            onInputChange={(val: string) => {
              isHydratingArrivalFromCodeRef.current = false;
              setArrivalSearchPort(val);
            }}
            options={mapSeaportOptions(arrivalSeaports)}
            onSelect={(item: any[]) => {
              hasUserSelectedArrivalPort.current = true;
              isHydratingArrivalFromCodeRef.current = false;
              const selected = item?.[0];
              setValue(`${fieldPrefix}arrival_port_code`, selected?.code || "", { shouldValidate: true });
              setValue(`${fieldPrefix}arrival_port_name`, selected?.value || "", { shouldValidate: true });
              setValue(`${fieldPrefix}arrival_city`, selected?.city_name || "", { shouldValidate: true });
              setValue(`${fieldPrefix}arrival_country_code`, selected?.country_iso_code || "", { shouldValidate: true });
              setValue(`${fieldPrefix}arrival_country_name`, selected?.country_name || "", { shouldValidate: true });
              trigger(`${fieldPrefix}arrival_port_name`);
            }}
            filterBy={["label", "city_name", "country_name", "code"]}
            handleRenderOption={handleRenderAddressOption}
            isLoading={isArrivalSeaportsLoading}
            label={intl.formatMessage({
              id: "tracking.components.editScheduleDialog.arrivalPort",
              defaultMessage: "Arrival port",
            })}
            required
          />
        </div>
        <div className="w-192 align-date">
          <div className="font-b3 mb-2">
            <FormattedMessage id="quote.provider.vesselArrivalDate" defaultMessage="Vessel arrival date" />
            <span className="text-danger ml-1">*</span>
          </div>
          <PickupDeliveryDate
            datePickerProps={{
              name: `vessels.${index}.vessel_arrival_date`,
              required: true,
              shouldUnregister: true,
            }}
            countries={countries}
            holidays={holidays}
            setHolidays={setHolidays}
          />
        </div>
      </div>
    </div>
  );
}

export default function EditScheduleMultistopVesselForm() {
  const methods = useFormContext<IEditScheduleForm>();
  const { fields, append, remove } = useFieldArray({
    name: "vessels",
    control: methods.control,
    shouldUnregister: true,
  });

  const { data: countries = [] } = useGetCountries({
    refetchOnMount: false,
  });

  return (
    <>
      {fields.map((vessel, index) => (
        <div key={vessel.id} className="mb-3xl">
          <VesselEntry index={index} countries={countries} remove={remove} />
        </div>
      ))}
      <button
        type="button"
        className="text-blue-200 font-b4 bg-transparent border-0 p-0 cursor-pointer"
        onClick={() => append(defaultVesselInfo)}
      >
        <FormattedMessage id="quote.provider.addVessel" defaultMessage="Add vessel" />
      </button>
    </>
  );
}
